import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { employerService } from '@/services/api/employerService';

// Types
export interface Candidate {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  status: 'pending' | 'scheduled' | 'completed' | 'rejected';
  interviewDate?: string;
  score?: number;
  resumeUrl?: string;
  createdAt: string;
}

export interface InterviewConfig {
  uid: string;
  title: string;
  duration: number;
  questionsCount: number;
  skillsToAssess: string[];
  difficultyLevel: 'easy' | 'medium' | 'hard';
  isActive: boolean;
}

export interface ATSConfig {
  uid: string;
  provider: string;
  apiKey?: string;
  webhookUrl?: string;
  isActive: boolean;
}

export interface Plan {
  uid: string;
  name: string;
  interviewsPerMonth: number;
  candidatesLimit: number;
  features: string[];
  price: number;
  isActive: boolean;
}

export interface DashboardStats {
  totalCandidates: number;
  pendingInterviews: number;
  completedInterviews: number;
  averageScore: number;
  thisMonthInterviews: number;
}

interface EmployerState {
  candidates: Candidate[];
  selectedCandidate: Candidate | null;
  interviewConfigs: InterviewConfig[];
  atsConfig: ATSConfig | null;
  currentPlan: Plan | null;
  dashboardStats: DashboardStats | null;
  apiKeys: { uid: string; key: string; name: string; createdAt: string }[];
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Initial state
const initialState: EmployerState = {
  candidates: [],
  selectedCandidate: null,
  interviewConfigs: [],
  atsConfig: null,
  currentPlan: null,
  dashboardStats: null,
  apiKeys: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

// Async thunks
export const fetchCandidates = createAsyncThunk(
  'employer/fetchCandidates',
  async (
    params: { page?: number; limit?: number; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await employerService.getCandidates(params);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch candidates';
      return rejectWithValue(message);
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'employer/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employerService.getDashboardStats();
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch dashboard stats';
      return rejectWithValue(message);
    }
  }
);

export const fetchInterviewConfigs = createAsyncThunk(
  'employer/fetchInterviewConfigs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employerService.getInterviewConfigs();
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch interview configs';
      return rejectWithValue(message);
    }
  }
);

export const createInterviewConfig = createAsyncThunk(
  'employer/createInterviewConfig',
  async (config: Omit<InterviewConfig, 'uid'>, { rejectWithValue }) => {
    try {
      const response = await employerService.createInterviewConfig(config);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to create interview config';
      return rejectWithValue(message);
    }
  }
);

export const updateInterviewConfig = createAsyncThunk(
  'employer/updateInterviewConfig',
  async (config: InterviewConfig, { rejectWithValue }) => {
    try {
      const response = await employerService.updateInterviewConfig(config);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to update interview config';
      return rejectWithValue(message);
    }
  }
);

export const fetchCurrentPlan = createAsyncThunk(
  'employer/fetchCurrentPlan',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employerService.getCurrentPlan();
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch current plan';
      return rejectWithValue(message);
    }
  }
);

export const generateApiKey = createAsyncThunk(
  'employer/generateApiKey',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await employerService.generateApiKey(name);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to generate API key';
      return rejectWithValue(message);
    }
  }
);

export const fetchApiKeys = createAsyncThunk(
  'employer/fetchApiKeys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employerService.getApiKeys();
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch API keys';
      return rejectWithValue(message);
    }
  }
);

export const deleteApiKey = createAsyncThunk(
  'employer/deleteApiKey',
  async (uid: string, { rejectWithValue }) => {
    try {
      await employerService.deleteApiKey(uid);
      return uid;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete API key';
      return rejectWithValue(message);
    }
  }
);

export const registerCandidate = createAsyncThunk(
  'employer/registerCandidate',
  async (
    candidate: {
      name: string;
      email: string;
      phone?: string;
      resumeUrl?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await employerService.registerCandidate(candidate);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to register candidate';
      return rejectWithValue(message);
    }
  }
);

// Slice
const employerSlice = createSlice({
  name: 'employer',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setSelectedCandidate: (state, action: PayloadAction<Candidate | null>) => {
      state.selectedCandidate = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; limit?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.pagination.limit = action.payload.limit;
      }
    },
  },
  extraReducers: builder => {
    // Fetch Candidates
    builder
      .addCase(fetchCandidates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates = action.payload.candidates;
        state.pagination.total = action.payload.total;
      })
      .addCase(fetchCandidates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Dashboard Stats
    builder
      .addCase(fetchDashboardStats.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Interview Configs
    builder.addCase(fetchInterviewConfigs.fulfilled, (state, action) => {
      state.interviewConfigs = action.payload;
    });

    // Create Interview Config
    builder.addCase(createInterviewConfig.fulfilled, (state, action) => {
      state.interviewConfigs.push(action.payload);
    });

    // Update Interview Config
    builder.addCase(updateInterviewConfig.fulfilled, (state, action) => {
      const index = state.interviewConfigs.findIndex(
        c => c.uid === action.payload.uid
      );
      if (index !== -1) {
        state.interviewConfigs[index] = action.payload;
      }
    });

    // Fetch Current Plan
    builder.addCase(fetchCurrentPlan.fulfilled, (state, action) => {
      state.currentPlan = action.payload;
    });

    // Fetch API Keys
    builder.addCase(fetchApiKeys.fulfilled, (state, action) => {
      state.apiKeys = action.payload;
    });

    // Generate API Key
    builder.addCase(generateApiKey.fulfilled, (state, action) => {
      state.apiKeys.push(action.payload);
    });

    // Delete API Key
    builder.addCase(deleteApiKey.fulfilled, (state, action) => {
      state.apiKeys = state.apiKeys.filter(key => key.uid !== action.payload);
    });

    // Register Candidate
    builder
      .addCase(registerCandidate.pending, state => {
        state.isLoading = true;
      })
      .addCase(registerCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates.unshift(action.payload);
      })
      .addCase(registerCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPagination } = employerSlice.actions;

export default employerSlice.reducer;
